import { useState, useEffect, useMemo, useCallback, useContext } from 'react';
import { AppContext } from '../../../context/AppContext';
import { Col, Form, Button } from 'react-bootstrap';
import { createColumnHelper } from '@tanstack/react-table';
import DataTable from '../../../component/wallet/data-table/DataTable';
import DataTableToolbar from '../../../component/wallet/data-table/DataTableToolbar';
import StatusBadge from '../../../component/wallet/StatusBadge';
import CurrencyDisplay from '../../../component/wallet/CurrencyDisplay';
import ConfirmDialog from '../../../component/wallet/ConfirmDialog';
import { useAdmin } from '../../../Hooks/wallet/useadmin';
import { useDebounce } from '../../../Hooks/wallet/usedebounce';
import { formatDate } from '../../../lib/wallet/utils';
import { HOLD_STATUSES, WALLET_TYPES, DEFAULT_PAGE_SIZE } from '../../../lib/wallet/constants';

const columnHelper = createColumnHelper();

const HoldsPage = () => {
    const { sidebar } = useContext(AppContext);
    const { loading, fetchHolds, doCaptureHold, doReleaseHold } = useAdmin();
    const [holds, setHolds] = useState([]);
    const [statusFilter, setStatusFilter] = useState('');
    const [walletTypeFilter, setWalletTypeFilter] = useState('');
    const [walletIdInput, setWalletIdInput] = useState('');
    const [pagination, setPagination] = useState({
        page: 1,
        limit: DEFAULT_PAGE_SIZE,
        total_count: 0,
        total_pages: 1,
    });

    const walletIdFilter = useDebounce(walletIdInput, 500);

    const loadHolds = useCallback(async (page = 1) => {
        try {
            const res = await fetchHolds({
                page,
                limit: pagination.limit,
                status: statusFilter || undefined,
                wallet_type: walletTypeFilter || undefined,
                wallet_id: walletIdFilter ? Number(walletIdFilter) : undefined,
            });
            setHolds(res?.data?.holds || []);
            setPagination((prev) => ({ ...prev, page, ...res?.data?.pagination }));
        } catch {
            // handled by hook
        }
    }, [fetchHolds, pagination.limit, statusFilter, walletTypeFilter, walletIdFilter]);

    useEffect(() => {
        loadHolds(1);
    }, [statusFilter, walletTypeFilter, walletIdFilter]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleCapture = useCallback(async (holdId) => {
        const confirmed = await ConfirmDialog({
            title: 'Capture Hold',
            message: `Capture hold #${holdId}? This will deduct the amount from the wallet balance.`,
        });
        if (!confirmed) return;
        try {
            await doCaptureHold({ hold_id: holdId });
            setHolds((prev) => prev.map((h) => h.hold_id === holdId ? { ...h, status: 'captured' } : h));
        } catch {
            // handled by hook
        }
    }, [doCaptureHold]);

    const handleRelease = useCallback(async (holdId) => {
        const confirmed = await ConfirmDialog({
            title: 'Release Hold',
            message: `Release hold #${holdId}? This will free the reserved funds.`,
        });
        if (!confirmed) return;
        try {
            await doReleaseHold({ hold_id: holdId });
            setHolds((prev) => prev.map((h) => h.hold_id === holdId ? { ...h, status: 'released' } : h));
        } catch {
            // handled by hook
        }
    }, [doReleaseHold]);

    const columns = useMemo(() => [
        columnHelper.accessor('hold_id', { header: 'ID', size: 60 }),
        columnHelper.accessor('full_name', {
            header: 'User',
            cell: (info) => (
                <div>
                    <div className="fw-medium">{info.getValue()}</div>
                    <div className="small text-muted">{info.row.original.email}</div>
                </div>
            ),
        }),
        columnHelper.accessor('wallet_id', { header: 'Wallet ID', size: 80 }),
        columnHelper.accessor('amount', {
            header: 'Amount',
            cell: (info) => (
                <CurrencyDisplay amount={info.getValue()} currencySymbol={info.row.original.currency_symbol} />
            ),
        }),
        columnHelper.accessor('wallet_type', {
            header: 'Wallet Type',
            cell: (info) => <StatusBadge status={info.getValue()} />,
        }),
        columnHelper.accessor('status', {
            header: 'Status',
            cell: (info) => {
                const status = info.getValue();
                const expired = info.row.original.effectively_expired;
                return (
                    <span className="d-flex align-items-center gap-1">
                        <StatusBadge status={status} />
                        {expired && status === 'active' && (
                            <span className="badge bg-secondary">Expired</span>
                        )}
                    </span>
                );
            },
        }),
        columnHelper.accessor('reference_id', {
            header: 'Reference',
            cell: (info) => {
                const refId = info.getValue();
                const refType = info.row.original.reference_type;
                if (!refId) return '—';
                return refType ? `${refType}: ${refId}` : refId;
            },
        }),
        columnHelper.accessor('expires_at', {
            header: 'Expires',
            cell: (info) => (info.getValue() ? formatDate(info.getValue(), 'MMM dd, yyyy') : '—'),
        }),
        columnHelper.accessor('created_at', {
            header: 'Created',
            cell: (info) => formatDate(info.getValue()),
        }),
        columnHelper.display({
            id: 'actions',
            header: 'Actions',
            cell: ({ row }) => {
                const { hold_id, status, effectively_expired } = row.original;
                const canAct = status === 'active' && !effectively_expired;
                return canAct ? (
                    <div className="d-flex gap-1">
                        <Button size="sm" variant="outline-success" onClick={() => handleCapture(hold_id)}>
                            Capture
                        </Button>
                        <Button size="sm" variant="outline-danger" onClick={() => handleRelease(hold_id)}>
                            Release
                        </Button>
                    </div>
                ) : '—';
            },
        }),
    ], [handleCapture, handleRelease]);

    return (
        <div className={sidebar ? "taskMain " : "cx-active taskMain"} id="cx-main">
            <div className="role-content-main">
            <div className="sub-header-role mb-4">
                <h6 className="mt-2 role-text">Holds</h6>
            </div>
                <div className="fade-in">
            

            <DataTableToolbar
                filters={[
                    {
                        value: statusFilter,
                        onChange: setStatusFilter,
                        placeholder: 'All Statuses',
                        options: HOLD_STATUSES,
                    },
                    {
                        value: walletTypeFilter,
                        onChange: setWalletTypeFilter,
                        placeholder: 'All Wallet Types',
                        options: WALLET_TYPES,
                    },
                ]}
            >
                {/* <Col xs={6} md={2}>
                    <Form.Control
                        size="sm"
                        type="number"
                        placeholder="Wallet ID…"
                        value={walletIdInput}
                        onChange={(e) => setWalletIdInput(e.target.value)}
                    />
                </Col> */}
            </DataTableToolbar>

            <DataTable
                columns={columns}
                data={holds}
                loading={loading}
                page={pagination.page}
                pageSize={pagination.limit}
                totalCount={pagination.total_count}
                totalPages={pagination.total_pages}
                onPageChange={(p) => loadHolds(p)}
                onPageSizeChange={(s) => setPagination((prev) => ({ ...prev, limit: s }))}
                emptyTitle="No holds found"
                emptyDescription="No holds match the current filters."
            />
        </div>
        </div>
        </div>
    );
};

export default HoldsPage;
